import React, { useState, useEffect } from 'react';
import { Badge, Card, EmptyState, Loader } from '../../../../ui';
import { kysForm } from '../../../../Api/apiUtils';
import './kys-form.scss';

interface Field {
  label: string;
  type: string;
  answer?: string;
  textAreaEnabledForYes?: { answer?: string };
  inputFields?: Field[];
}

interface FormGroup {
  title: string;
  formFields: Field[];
}

interface KysFormData {
  form: {
    formGroup: FormGroup[];
  };
}

interface KYSFormProps {
  profileData: any;
  courseId: string;
}

const AnswerValue: React.FC<{ value?: any }> = ({ value }) => {
  const str = value != null ? String(value) : "";
  if (!str.trim()) {
    return <span className="kys-answer ds-text-subtle">—</span>;
  }
  return <span className="kys-answer">{str}</span>;
};

const KYSForm: React.FC<KYSFormProps> = ({ profileData, courseId }) => {
  const [kysFormData, setKysFormData] = useState<KysFormData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const userId = profileData?.userId;

  useEffect(() => {
    fetchKysDetails();
  }, [courseId, userId]);

  const fetchKysDetails = async () => {
    try {
      setLoading(true);
      const response = await kysForm(courseId, userId);
      setKysFormData(response.data);
    } catch (error) {
      console.error('Error fetching KYS details:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field: Field, index: number) => {
    switch (field.type) {
      case 'radio':
        return (
          <div key={index} className="kys-field">
            <div className="kys-question ds-text-muted">{field.label}</div>
            <div className="kys-answer-wrap">
              <span>
                <Badge tone={field.answer?.toLowerCase() === 'yes' ? 'success' : field.answer?.toLowerCase() === 'no' ? 'danger' : 'neutral'}>
                  {field.answer || '—'}
                </Badge>
              </span>
              {field.textAreaEnabledForYes?.answer && (
                <div className="kys-sub-answer ds-text-muted">{field.textAreaEnabledForYes.answer}</div>
              )}
            </div>
          </div>
        );
      case 'textarea':
        return (
          <div key={index} className="kys-field">
            <div className="kys-question ds-text-muted">{field.label}</div>
            <div className="kys-answer-wrap">
              <div className="kys-textarea-answer">{field.answer || '—'}</div>
            </div>
          </div>
        );
      case 'inputGroup':
        return (
          <div key={index} className="kys-field kys-field--group">
            <div className="kys-question ds-text-muted">{field.label}</div>
            <div className="kys-subfields">
              {field.inputFields?.map((inputField, inputIndex) => (
                <div key={inputIndex} className="kys-subfield">
                  <div className="kys-sublabel ds-text-muted">{inputField.label}</div>
                  <AnswerValue value={inputField.answer} />
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return (
          <div key={index} className="kys-field">
            <div className="kys-question ds-text-muted">{field.label}</div>
            <div className="kys-answer-wrap">
              <AnswerValue value={field.answer || field.textAreaEnabledForYes?.answer} />
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <Loader label="Loading KYS form…" />
    );
  }

  if (!kysFormData || kysFormData.form.formGroup.length === 0) {
    return (
      <EmptyState
        title="No KYS form data available"
        description="The Know Your Student form hasn't been filled for this course yet."
      />
    );
  }

  return (
    <div className="kys-container">
      {kysFormData.form.formGroup.map((group, groupIndex) => (
        <Card
          key={groupIndex}
          flush
          title={
            <span className="kys-section-title">
              <span className="kys-section-icon" aria-hidden>{groupIndex + 1}</span>
              {group.title}
            </span>
          }
        >
          <div className="kys-fields">
            {group.formFields.map((field, fieldIndex) => renderField(field, fieldIndex))}
          </div>
        </Card>
      ))}
    </div>
  );
};

export default KYSForm;
